import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

/**
 * Shared empty-state card for views with no domain data yet.
 *
 * ADR-0005: this app ships with no seed data. Every domain row originates
 * from a real ingestion (RBC / Lloyds / Fund recon, COAC upload, KPI upload,
 * etc.) so most views render empty on a fresh session. Each list / dashboard
 * should render this card on the zero-data branch — never fabricate rows,
 * never crash on `Math.max(...empty)` or `arr.reduce(...)` on `[]`.
 *
 * Convention (issue #28):
 * - List views: use `<EmptyState />` in place of the table when the row set
 *   is empty, with a `hint` pointing at the upload surface (e.g.
 *   `{ label: 'Upload an RBC statement', href: '/ops/reconciliation' }`).
 * - Dashboards: use `<EmptyState title="No data uploaded yet" />` per
 *   zero-data section, or wrap the whole page body when the entire view
 *   depends on a single absent source.
 * - Pages that depend on shapes we can't yet reconstruct should wrap the
 *   body and explain in `body` what would populate them.
 *
 * Keep it visually quiet: this is the honest baseline, not a loud CTA.
 */
export interface EmptyStateProps {
    title: string;
    body?: ReactNode;
    hint?: { label: string; href?: string };
    className?: string;
}

export default function EmptyState({ title, body, hint, className }: EmptyStateProps) {
    return (
        <div className={cn('septima-empty-state', className)}>
            <h3>{title}</h3>
            {body && <div className="septima-empty-state__body">{body}</div>}
            {hint &&
                (hint.href ? (
                    <Link to={hint.href} className="septima-empty-state__link">
                        {hint.label}
                    </Link>
                ) : (
                    <div className="septima-empty-state__hint">{hint.label}</div>
                ))}
        </div>
    );
}
